<template>
  <div v-if="content" class="level" :class="[content.type, content.status]">
    <VideoLevel
      v-if="content.type === 'video'"
      :parentStatus="currentParent.status"
      :hasAccess="content.hasAccess"
      :nextVideo="nextVideo"
      :plansLink="plansLink"
      :quiz="content.quiz || null"
      :quizStatus="content.quiz ? content.quiz.status : ''"
      :title="content.file.name"
      :video="content.file"
      :videoStatus="content.status || defaultStatus"
      :instructorName="content.instructorName || null"
      @toggle-content="updateContext"
    />
    <LabLevel
      v-if="content.type === 'lab' && showLabLevel"
      :parentId="currentParent.id"
      :parentStatus="currentParent.status"
      :hasAccess="content.hasAccess"
      :content="content.file"
      :contentStatus="content.status || defaultStatus"
      :contentType="content.labType"
      :plansLink="plansLink"
      :title="content.file.name"
      @toggle-content="updateContext"
    />
    <IFrameLevel
      v-if="content.type === 'iframe'"
      :courseId="course.id"
      :courseStatus="course.status"
      :content="content.file"
      :contentStatus="content.status || defaultStatus"
      :plansLink="plansLink"
      :hasAccess="content.hasAccess"
      :title="content.file.name"
      @toggle-content="updateContext"
    />
    <QuizLevel
      v-if="content.type === 'quiz'"
      :courseId="course.id"
      :courseStatus="course.status"
      :content="content.file"
      :hasAccess="content.hasAccess"
      :plansLink="plansLink"
      :title="lastActivityContentName"
      :contentStatus="content.status || defaultStatus"
      :nextVideo="nextVideo"
      @toggle-content="updateContext"
    />
    <ExercisesLevel
      v-if="content.type === 'exercise_group'"
      :content="content"
      :hasAccess="content.hasAccess"
      :courseStatus="course.status"
      :plansLink="plansLink"
      :title="lastActivityContentName"
      :contentStatus="content.status || defaultStatus"
      @toggle-content="updateContext"
    />
    <CourseLevel
      v-if="content.type === 'course'"
      :courseId="level.uuid"
      :parentStatus="content.status"
      :hasAccess="content.hasAccess"
      :content="content"
      contentType="course"
      :plansLink="plansLink"
      :title="level.name"
      @toggle-content="updateContext"
    />
    <LinkLevel
      v-if="content.type === 'link'"
      :parentStatus="content.status"
      :hasAccess="true"
      :content="content"
      :contentStatus="content.status || defaultStatus"
      contentType="link"
      :plansLink="plansLink"
      :title="level.name"
      @toggle-content="updateContext"
    />
    <LearningPathLevel
      v-if="content.type === 'learning-path'"
      :learningPathId="level.uuid"
      :parentStatus="content.status"
      :hasAccess="content.hasAccess"
      :content="content"
      :contentType="content.type"
      :plansLink="plansLink"
      :title="level.name"
      @toggle-content="updateContext"
    />
  </div>
</template>

<script>
import { hasContentAccess, duration } from '@/helpers'
import { mapState, mapGetters, mapMutations } from 'vuex'
import CONSTANTS from '@/constants'
import VideoLevel from '@/components/content/VideoLevel.vue'
import LabLevel from '@/components/content/LabLevel.vue'
import IFrameLevel from '@/components/content/IFrameLevel.vue'
import QuizLevel from '@/components/content/QuizLevel.vue'
import ExercisesLevel from '@/components/content/ExercisesLevel.vue'
import CourseLevel from '@/components/content/CourseLevel.vue'
import LinkLevel from '@/components/content/LinkLevel.vue'
import LearningPathLevel from '@/components/content/LearningPathLevel.vue'

export default {
  name: 'Level',
  components: {
    VideoLevel,
    LabLevel,
    IFrameLevel,
    QuizLevel,
    ExercisesLevel,
    CourseLevel,
    LinkLevel,
    LearningPathLevel,
  },
  props: {
    level: {
      type: Object,
      required: true,
    },
    contentFor: {
      type: String,
      required: true,
    },
    parentId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      defaultStatus: CONSTANTS.CONTENT_USER_STATUS.UNSTARTED,
    }
  },
  computed: {
    ...mapState({
      course: state => state.courses.currentCourse,
      courseLabs: state => state.content.currentLabs,
      courseIFrames: state => state.content.currentIFrames,
      courseVideos: state => state.content.currentVideos,
      courseQuizzes: state => state.content.currentQuizzes,
      courseExercises: state => state.content.currentExercises,
      currentHoveredNote: state => state.content.currentHoveredNote,
      currentLinks: state => state.content.currentLinks,
      allLevels: state => state.courses.currentLevels,
      plansLink: state => state.app.plansLink,
      isAdmin: state => state.auth.isAdmin,
      isContentCreator: state => state.auth.isContentCreator,
      playlistCache: state => state.playlists.playlistCache,
    }),
    ...mapGetters({
      courseBookmarks: 'content/courseBookmarks',
    }),
    showLabLevel() {
      return (
        this.content.file.status === CONSTANTS.CONTENT_STATUS.PUBLISHED ||
        this.isAdmin ||
        this.isContentCreator
      )
    },
    nextVideo() {
      const currentIndex = this.allLevels.indexOf(this.level) + 1
      if (currentIndex === this.allLevels.length) return {}
      return (
        this.allLevels
          .slice(currentIndex)
          .find(
            level => level.content_type === CONSTANTS.CONTENT_TYPES.VIDEO
          ) || {}
      )
    },
    lastActivityContentName() {
      let lastContent = {}
      if (this.level.last_activity) {
        const {
          level: {
            last_activity: { content_type: type, uuid },
          },
        } = this
        if (type === CONSTANTS.CONTENT_TYPES.VIDEO) {
          lastContent = this.courseVideos.find(video => video.id === uuid)
        }
        if (type === CONSTANTS.CONTENT_TYPES.IFRAME) {
          lastContent = this.courseIFrames.find(iframe => iframe.id === uuid)
        }
        if (type === CONSTANTS.CONTENT_TYPES.LAB) {
          lastContent = this.courseLabs.find(lab => lab.id === uuid)
        }
        return `Test your knowledge: ${lastContent.name}`
      }
      return 'Test your knowledge'
    },
    content() {
      const {
        level: { content_type: type },
      } = this
      if (type === CONSTANTS.CONTENT_TYPES.VIDEO) {
        return this.createVideo()
      }
      if (type === CONSTANTS.CONTENT_TYPES.QUIZ) {
        return this.createQuiz()
      }
      if (type === CONSTANTS.CONTENT_TYPES.EXERCISE_GROUP) {
        return this.createExerciseGroup()
      }
      if (type === CONSTANTS.CONTENT_TYPES.IFRAME) {
        return this.createIframe()
      }
      if (type === CONSTANTS.CONTENT_TYPES.LAB) {
        return this.createLab()
      }
      if (type === CONSTANTS.PLAYLIST_CONTENT_TYPES.COURSE) {
        return this.createCourse()
      }
      if (type === CONSTANTS.PLAYLIST_CONTENT_TYPES.LINK) {
        return this.createLink()
      }
      if (type === CONSTANTS.PLAYLIST_CONTENT_TYPES.LEARNING_PATH) {
        return this.createLearningPath()
      }
      return undefined
    },
    currentParent() {
      if (this.contentFor === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST) {
        return this.playlistCache.find(pl => pl.id === this.parentId)
      }
      return this.course
    },
  },
  methods: {
    ...mapMutations({
      setParentContent: 'content/CURRENT_PARENT',
    }),
    createVideo() {
      const { content_type: type, uuid, quiz = null } = this.level
      const content = { type, uuid }
      content.instructorName = this.level.instructor_name
      const file = this.courseVideos.find(
        video => video.id === uuid && video.parent_id === this.currentParent?.id
      )
      const { user_status: status } = file
      if (quiz) {
        const quizContent = this.courseQuizzes.find(q => q.id === quiz.uuid)
        if (quizContent) {
          content.quiz = {
            ...quizContent,
            status: quizContent.user_status,
          }
        }
      }
      content.file = file
      content.status = status
      // TODO remove conditional hasAccess for playlist. Depends on Backend fix.
      content.hasAccess =
        this.contentFor === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST
          ? true
          : hasContentAccess({ parent: this.course, content })
      return content
    },
    createQuiz() {
      const { content_type: type, uuid } = this.level
      const content = { type, uuid }
      const file = this.courseQuizzes.find(quiz => quiz.id === uuid)
      const { user_status: status } = file || {}
      content.file = file
      content.status = status
      content.hasAccess = hasContentAccess({ parent: this.course, content })
      return content
    },
    createExerciseGroup() {
      const {
        content_type: type,
        uuid,
        exercises,
        last_activity: lastActivity,
      } = this.level
      const content = { type, uuid }
      const filteredExercises = exercises.reduce((acc, e) => {
        acc.push(this.courseExercises.find(exercise => exercise.id === e.uuid))
        return acc
      }, [])
      content.exercises = filteredExercises
      content.file = filteredExercises[0] // just to check access
      content.status = this.getExercisesGroupStatus(exercises)
      content.hasAccess = hasContentAccess({
        parent: this.course,
        content,
      })
      return content
    },
    createIframe() {
      const { content_type: type, uuid } = this.level
      const content = { type, uuid }
      const file = this.courseIFrames.find(iframe => iframe.id === uuid)
      const { user_status: status } = file
      content.file = file
      content.status = status
      content.hasAccess = hasContentAccess({ parent: this.course, content })
      return content
    },
    createLab() {
      const { content_type: type, uuid } = this.level
      const content = { type, uuid }
      const file =
        this.contentFor === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST
          ? this.courseLabs.find(
              lab =>
                lab.id === this.level.uuid &&
                lab.parent_id === this.currentParent?.id
            )
          : this.courseLabs.find(lab => lab.id === this.level.uuid)
      const { user_status: status, lab_type: labType } = file
      content.file = file
      content.status = status
      content.labType = labType
      // TODO remove conditional hasAccess for playlist. Depends on Backend fix.
      content.hasAccess =
        this.contentFor === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST
          ? true
          : hasContentAccess({
              parent: this.currentParent,
              content,
            })
      return content
    },
    createCourse() {
      const { content_type: type, uuid, slug, status } = this.level
      const content = { type, uuid, slug, status }
      content.learningArea = this.level.main_learning_area
        ? this.level.main_learning_area
        : this.level.learning_areas?.pop() ?? ''
      const instructorName = this.level.instructors?.length
        ? this.level.instructors[0].name
        : ''
      content.instructor =
        this.level.instructors?.length > 1
          ? `${instructorName} +${this.level.instructors?.length - 1}`
          : instructorName
      const activities = this.level.user_status
        ? this.level.user_status.total_exercises +
          this.level.user_status.total_iframes +
          this.level.user_status.total_labs +
          this.level.user_status.total_quizzes +
          this.level.user_status.total_videos
        : 0
      content.activities = `${activities} activities`
      content.progress = this.level.user_status?.progress
      if (content.progress && content.progress > 0) {
        content.status =
          content.progress < 1
            ? CONSTANTS.CONTENT_USER_STATUS.STARTED
            : CONSTANTS.CONTENT_USER_STATUS.FINISHED
      } else {
        content.status = CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
      }
      content.hasAccess = true
      return content
    },
    createLearningPath() {
      const {
        content_type: type,
        uuid,
        slug,
        status,
        main_learning_area,
        duration_in_seconds,
      } = this.level
      const content = { type, uuid, slug, status }
      content.learningArea =
        main_learning_area || (this.level.learning_areas?.pop() ?? '')
      const instructorName = this.level.instructors?.length
        ? this.level.instructors[0].name
        : ''
      content.instructor =
        this.level.instructors?.length > 1
          ? `${instructorName} +${this.level.instructors?.length - 1}`
          : instructorName
      content.duration = duration
        .durationFromSecs(duration_in_seconds, 'zeros')
        .replace('h', 'h ')
      content.progress = this.level.user_status?.progress
      if (content.progress && content.progress > 0) {
        content.status =
          content.progress < 100
            ? CONSTANTS.CONTENT_USER_STATUS.STARTED
            : CONSTANTS.CONTENT_USER_STATUS.FINISHED
      } else {
        content.status = CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
      }
      content.hasAccess = true
      return content
    },
    createLink() {
      const content = this.currentLinks.find(
        l =>
          l.uuid === this.level.uuid && l.parent_id === this.currentParent?.id
      )
      const {
        content_type: type,
        uuid,
        link_url: link,
        name,
        user_status: status,
      } = content
      return { type, uuid, link, name, status }
    },
    getExercisesGroupStatus(exercises) {
      let current = null
      const statusArray = exercises.reduce((acc, e) => {
        current = this.courseExercises.find(ex => ex.id === e.uuid)
        if (acc[current.user_status]) {
          acc[current.user_status] += 1
        } else {
          acc[current.user_status] = 1
        }
        return acc
      }, {})
      if (
        statusArray[CONSTANTS.CONTENT_USER_STATUS.FINISHED] &&
        statusArray[CONSTANTS.CONTENT_USER_STATUS.FINISHED] === exercises.length
      ) {
        return CONSTANTS.CONTENT_USER_STATUS.FINISHED
      }
      if (
        statusArray[CONSTANTS.CONTENT_USER_STATUS.STARTED] ||
        statusArray[CONSTANTS.CONTENT_USER_STATUS.FINISHED]
      ) {
        return CONSTANTS.CONTENT_USER_STATUS.STARTED
      }
      return CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
    },
    updateContext(type) {
      if (this.contentFor === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST) {
        this.setParentContent({ ...this.currentParent, type: 'playlist' })
      }
      if (type !== CONSTANTS.CONTENT_TYPES.VIDEO) {
        this.$store.dispatch('content/setPlayingVideoId', '')
        this.$store.dispatch('content/setPlayingVideoTime', 0)
      }
      if (type !== CONSTANTS.CONTENT_TYPES.QUIZ) {
        this.$store.dispatch('content/setCurrentQuizId', '')
      }
      if (type !== CONSTANTS.CONTENT_TYPES.EXERCISE_GROUP) {
        this.$store.dispatch('content/setCurrentExercisesId', '')
      }
      if (type !== CONSTANTS.CONTENT_TYPES.IFRAME) {
        this.$store.dispatch('iframes/setPlayingIFrameId', '')
      }
    },
  },
}
</script>
